import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Check, Loader2, Phone, Calendar } from "lucide-react";

const SIZES = [
  { v: "1-10", l: "1 – 10 dipendenti" },
  { v: "11-50", l: "11 – 50 dipendenti" },
  { v: "51-200", l: "51 – 200 dipendenti" },
  { v: "200+", l: "Oltre 200 dipendenti" },
];

export function DemoRequestModal({ open, onOpenChange, source = "demo", triggerLabel }) {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", company_name: "", company_size: "11-50", message: "",
  });

  const up = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Compila nome, email e telefono");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, interest: source });
      setDone(true);
      toast.success("Richiesta inviata! Ti contatteremo entro 24h.");
    } catch (err) {
      toast.error("Errore invio richiesta");
    } finally { setLoading(false); }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setDone(false);
      setForm({ full_name: "", email: "", phone: "", company_name: "", company_size: "11-50", message: "" });
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => v ? onOpenChange(true) : close()}>
      <DialogContent className="rounded-3xl border-0 shadow-2xl max-w-xl p-0 overflow-hidden" data-testid="demo-modal">
        {!done ? (
          <>
            <div className="bg-[#0A0A0A] text-white p-6 md:p-8 relative overflow-hidden">
              <div className="orb orb-orange" style={{width: 300, height: 300, top: -150, right: -50, opacity: 0.3}}/>
              <div className="relative">
                <div className="inline-flex items-center gap-2 bg-[#FF7A29]/20 text-[#FF7A29] text-xs font-semibold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full mb-4">
                  <Calendar className="w-3.5 h-3.5"/> Demo personalizzata · 20 min
                </div>
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl md:text-3xl tracking-tight text-white">
                    Parliamone.<br/><span className="text-[#FF7A29]">20 minuti possono cambiare il tuo HR.</span>
                  </DialogTitle>
                </DialogHeader>
                <p className="text-sm text-white/70 mt-3">
                  Un consulente Tool Badge ti mostrerà la piattaforma in azione sul tuo caso d'uso reale.
                </p>
              </div>
            </div>
            <form onSubmit={submit} className="p-6 md:p-8 space-y-4 bg-white">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nome e cognome *</Label>
                  <Input data-testid="demo-name" required className="rounded-xl mt-1 h-11" value={form.full_name} onChange={up("full_name")}/>
                </div>
                <div>
                  <Label>Azienda</Label>
                  <Input data-testid="demo-company" className="rounded-xl mt-1 h-11" value={form.company_name} onChange={up("company_name")}/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Email lavoro *</Label>
                  <Input data-testid="demo-email" type="email" required className="rounded-xl mt-1 h-11" value={form.email} onChange={up("email")}/>
                </div>
                <div>
                  <Label>Telefono *</Label>
                  <Input data-testid="demo-phone" type="tel" required className="rounded-xl mt-1 h-11" value={form.phone} onChange={up("phone")} placeholder="+39 "/>
                </div>
              </div>
              <div>
                <Label>Dimensione azienda</Label>
                <Select value={form.company_size} onValueChange={(v)=>setForm({...form, company_size: v})}>
                  <SelectTrigger data-testid="demo-size" className="rounded-xl mt-1 h-11"><SelectValue/></SelectTrigger>
                  <SelectContent>{SIZES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cosa vorresti risolvere? (opzionale)</Label>
                <Textarea data-testid="demo-message" className="rounded-xl mt-1 min-h-[80px]" value={form.message} onChange={up("message")} placeholder="Es. gestione presenze multi-sede, rimborsi mensili, firma contratti…"/>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-[#EDE5D8]">
                <div className="flex items-center gap-2 text-xs text-[#757575]">
                  <Phone className="w-3.5 h-3.5 text-[#FF7A29]"/> Risposta garantita entro 24h
                </div>
                <Button type="submit" disabled={loading} data-testid="demo-submit" className="rounded-full h-11 px-6 bg-[#FF7A29] hover:bg-[#E85D00] btn-glow">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : null}
                  {triggerLabel || "Richiedi demo"}
                </Button>
              </div>
              <p className="text-[10px] text-[#757575]">
                Inviando questa richiesta accetti l'informativa privacy. Non useremo i tuoi dati per spam.
              </p>
            </form>
          </>
        ) : (
          <div className="p-10 text-center bg-white" data-testid="demo-success">
            <div className="w-20 h-20 bg-[#FF7A29] rounded-full mx-auto mb-6 flex items-center justify-center shadow-xl shadow-orange-300"><Check className="w-10 h-10 text-white" strokeWidth={3}/></div>
            <h2 className="font-display text-3xl mb-2">Richiesta ricevuta</h2>
            <p className="text-sm text-[#555] mb-6 max-w-sm mx-auto">
              Un consulente Tool Badge ti contatterà a <strong>{form.email}</strong> o al numero <strong>{form.phone}</strong> entro 24 ore lavorative.
            </p>
            <Button onClick={close} className="rounded-full bg-[#0A0A0A] hover:bg-[#FF7A29] h-11 px-8">Chiudi</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
